import React, { useState, useEffect } from "react";
import { compareField, money } from "../lib/dealerResponses";

/* ── Send to a dealer, and read what came back ──────────────────
   The household's own end of the dealer-link system. Two panels, both
   rendered as screens inside BuyingModal.

   ── HomeFleet does not send anything ──
   This panel produces a LINK and a block of text. A person copies both
   into their own email client and presses send there. Nothing here
   contacts a dealer, there is no send button that reaches outside the
   browser, and Clyde has no tool that mints a link — that is the
   structural half of "Clyde never contacts a dealer": not a rule he is
   asked to follow, a capability that does not exist.

   ── Dealer text is rendered raw here, on purpose ──
   Clyde's copy of a dealer's words is fenced as untrusted input
   (src/lib/untrustedText.js) because it is entering a model's context.
   The household's copy is NOT fenced: they are deciding whether to
   trust this dealer, and showing them a marked-up or filtered version
   of what was typed would take that judgement away. React escapes on
   render, so raw text is displayed as text and never as markup —
   nothing here uses dangerouslySetInnerHTML, and nothing should.

   ── One shared comparison ──
   compareField is imported from the same module that builds Clyde's
   DEALER RESPONSES block. Two implementations of "the dealer is $1,200
   higher" would eventually disagree, and the household would see one
   number on screen while Clyde said another in the next breath. */

const T_FALLBACK = { card: "#fff", cardAlt: "#f8fafc", border: "#e2e8f0", text: "#1e293b", textMd: "#475569", textSm: "#94a3b8", teal: "#0d9488", tealL: "#ccfbf1", red: "#dc2626", redL: "#fee2e2", green: "#16a34a", greenL: "#dcfce7", amber: "#b45309", amberL: "#fef3c7", slateL: "#f1f5f9" };

export function SendToDealerPanel({ T = T_FALLBACK, dealId, snapshot, onMinted, onBack }) {
  const [dealerLabel, setDealerLabel] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // The minted link, held in component state only. It is never stored and
  // never fetched again — see the copy block's own note.
  const [minted, setMinted] = useState(null);
  const [links, setLinks] = useState([]);
  const [copied, setCopied] = useState("");

  const loadLinks = React.useCallback(() => {
    if (!dealId) return;
    fetch(`/api/deals/${dealId}/links`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setLinks(d.links || []); })
      .catch(() => {});
  }, [dealId]);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  const generate = async () => {
    setErr("");
    if (!dealerLabel.trim()) { setErr("Add the dealership's name so you can tell these links apart later."); return; }
    if (!snapshot || snapshot.offer == null) { setErr("There's no offer on the worksheet to send yet."); return; }
    setBusy(true);
    try {
      // Order matters and is not interchangeable. The revision is
      // recorded FIRST because a link pins itself to the latest
      // offer_sent — mint first and it would point at a previous offer,
      // or at nothing at all on a deal that has never been sent.
      const revRes = await fetch(`/api/deals/${dealId}/revisions`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind: "offer_sent", dealerLabel: dealerLabel.trim(), note: note.trim() || null, snapshot }),
      });
      const rev = await revRes.json().catch(() => ({}));
      if (!revRes.ok) { setErr(rev.error || "Could not record this offer."); setBusy(false); return; }

      const linkRes = await fetch(`/api/deals/${dealId}/links`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ purpose: "offer", dealerLabel: dealerLabel.trim() }),
      });
      const link = await linkRes.json().catch(() => ({}));
      if (!linkRes.ok || !link.token) {
        // The offer IS recorded at this point. Saying so matters: the
        // household can generate a link again without re-entering
        // anything, and the record is not wrong, just incomplete.
        setErr((link.error || "Could not generate the link.") + " Your offer was recorded — try generating the link again.");
        setBusy(false); loadLinks(); return;
      }
      setMinted({ token: link.token, dealerLabel: dealerLabel.trim(), expiresAt: link.expiresAt, revisionSeq: link.revisionSeq });
      setDealerLabel(""); setNote("");
      loadLinks();
      onMinted && onMinted();
    } catch {
      setErr("Connection problem — nothing was sent. Try again.");
    }
    setBusy(false);
  };

  const revoke = async (tokenHash) => {
    try {
      const r = await fetch(`/api/deals/${dealId}/links`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tokenHash }),
      });
      if (r.ok) loadLinks();
    } catch {}
  };

  const url = minted ? `${window.location.origin}/?deal=${encodeURIComponent(minted.token)}` : "";
  const draft = minted
    ? `Hi${minted.dealerLabel ? " " + minted.dealerLabel : ""},\n\n` +
      `We'd like to make an offer on a ${snapshot.vehicle}.\n\n` +
      `Everything is laid out here${snapshot && snapshot.trade ? ", including our trade" : ""} — you can approve it, counter it, or tell us it doesn't work, all on that page:\n${url}\n\n` +
      `The link works once and expires in a week.\n\nThank you.`
    : "";

  const copy = (text, which) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(which);
      setTimeout(() => setCopied(""), 2000);
    }).catch(() => setErr("Couldn't copy automatically — select the text and copy it manually."));
  };

  const field = { width: "100%", boxSizing: "border-box", padding: "10px 12px", border: "1.5px solid " + T.border, borderRadius: 8, fontSize: 13.5, color: T.text, background: T.card, fontFamily: "inherit" };

  return (
    <div style={{ padding: 20 }}>
      {!minted ? (
        <>
          <div style={{ fontSize: 13, color: T.textMd, lineHeight: 1.6, marginBottom: 16 }}>
            This creates a private link showing this dealer your offer, your trade and your terms. They can approve it,
            counter it, or decline — and whatever they do lands back here. <strong style={{ color: T.text }}>HomeFleet doesn't
            email anyone.</strong> You copy the link into your own email and send it yourself.
          </div>

          <div style={{ fontSize: 12, color: T.textSm, marginBottom: 5 }}>Which dealership?</div>
          <input value={dealerLabel} onChange={(e) => setDealerLabel(e.target.value)} maxLength={200}
            placeholder="e.g. Toyota of Plano" style={{ ...field, marginBottom: 12 }} />

          <div style={{ fontSize: 12, color: T.textSm, marginBottom: 5 }}>Anything you want to say to them? (optional)</div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={3}
            placeholder="e.g. We're ready to move this week and financing is already approved."
            style={{ ...field, marginBottom: 14, resize: "vertical" }} />

          {snapshot && snapshot.offer != null ? (
            <div style={{ background: T.cardAlt, border: "1px solid " + T.border, borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase", color: T.textSm, marginBottom: 7 }}>What they'll see</div>
              <Row T={T} k="Vehicle" v={snapshot.vehicle} />
              <Row T={T} k="Your offer" v={money(snapshot.offer)} />
              <Row T={T} k="Asking price" v={money(snapshot.askingPrice)} />
              <Row T={T} k="Trade allowance" v={snapshot.trade ? money(snapshot.trade.allowance) : null} />
              <Row T={T} k="Financing" v={snapshot.rate != null ? snapshot.rate + "% over " + snapshot.termMonths + " months" : null} />
              <div style={{ fontSize: 11.5, color: T.textSm, marginTop: 8, lineHeight: 1.5 }}>
                Your name, email and phone are <strong>not</strong> included. They're shared only once you confirm a deal.
              </div>
            </div>
          ) : null}

          {err ? <div style={{ fontSize: 12.5, color: T.red, background: T.redL, borderRadius: 8, padding: "9px 11px", marginBottom: 12, lineHeight: 1.5 }}>{err}</div> : null}

          <button onClick={generate} disabled={busy} style={{ width: "100%", padding: "12px 0", background: busy ? T.slateL : "linear-gradient(135deg," + T.teal + ",#0f766e)", color: busy ? T.textSm : "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 14 }}>
            {busy ? "Generating..." : "Generate a link for this dealer"}
          </button>
        </>
      ) : (
        <>
          <div style={{ background: T.greenL, border: "1px solid #86efac", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.green, marginBottom: 4 }}>Link ready for {minted.dealerLabel}</div>
            <div style={{ fontSize: 12.5, color: T.textMd, lineHeight: 1.55 }}>
              Copy it now — <strong>this is the only time it's shown.</strong> It isn't stored anywhere we can read it back,
              so if you lose it you'll generate a new one rather than recover this.
            </div>
          </div>

          <div style={{ fontSize: 12, color: T.textSm, marginBottom: 5 }}>The link</div>
          <div style={{ ...field, wordBreak: "break-all", fontSize: 12, background: T.cardAlt, marginBottom: 8 }}>{url}</div>
          <button onClick={() => copy(url, "url")} style={btn(T)}>{copied === "url" ? "Copied" : "Copy link"}</button>

          <div style={{ fontSize: 12, color: T.textSm, margin: "16px 0 5px" }}>Or copy a message with it already in</div>
          <div style={{ ...field, whiteSpace: "pre-wrap", fontSize: 12.5, background: T.cardAlt, marginBottom: 8, lineHeight: 1.55 }}>{draft}</div>
          <button onClick={() => copy(draft, "draft")} style={btn(T)}>{copied === "draft" ? "Copied" : "Copy message"}</button>

          {err ? <div style={{ fontSize: 12.5, color: T.red, marginTop: 10 }}>{err}</div> : null}

          <button onClick={() => { setMinted(null); setErr(""); }} style={{ ...btn(T), marginTop: 16, background: T.card, color: T.teal, border: "1.5px solid " + T.teal }}>
            Send to another dealer
          </button>
        </>
      )}

      {links.length ? (
        <div style={{ marginTop: 24, paddingTop: 18, borderTop: "1px solid " + T.border }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase", color: T.textSm, marginBottom: 10 }}>Links you've sent</div>
          {links.map((l) => {
            const state = l.usedAt ? "Answered" : l.revokedAt ? "Withdrawn" : new Date(l.expiresAt) < new Date() ? "Expired" : "Waiting";
            const tone = state === "Answered" ? T.green : state === "Waiting" ? T.teal : T.textSm;
            return (
              <div key={l.tokenHash} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderTop: "1px solid " + T.border }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{l.dealerLabel || "Unnamed dealer"}</div>
                  <div style={{ fontSize: 11.5, color: T.textSm }}>
                    {state} · sent {fmtDate(l.createdAt) || "recently"}
                    {state === "Waiting" && fmtDate(l.expiresAt) ? " · expires " + fmtDate(l.expiresAt) : ""}
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: tone }}>{state}</span>
                {state === "Waiting" ? (
                  <button onClick={() => revoke(l.tokenHash)} style={{ background: "none", border: "1px solid " + T.border, borderRadius: 7, color: T.textMd, fontSize: 11.5, fontWeight: 600, padding: "5px 9px" }}>
                    Withdraw
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      <button onClick={onBack} style={{ ...btn(T), marginTop: 20, background: T.card, color: T.textMd, border: "1.5px solid " + T.border }}>
        Back to the worksheet
      </button>
    </div>
  );
}

export function DealerResponsesPanel({ T = T_FALLBACK, onBack }) {
  const [deals, setDeals] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetch("/api/deal-responses")
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { if (!cancelled) setDeals(d.deals || []); })
      .catch(() => { if (!cancelled) setErr("Couldn't load dealer responses right now."); });
    return () => { cancelled = true; };
  }, []);

  if (err) return <div style={{ padding: 20, fontSize: 13, color: T.red }}>{err}</div>;
  if (deals === null) return <div style={{ padding: 20, fontSize: 13, color: T.textSm }}>Loading responses...</div>;

  const withReplies = deals.filter((d) => d.responses && d.responses.length);
  if (!withReplies.length) {
    return (
      <div style={{ padding: 20 }}>
        <div style={{ fontSize: 13, color: T.textMd, lineHeight: 1.6 }}>
          No dealer has replied yet. Once you send a link and a dealer answers it, their response shows up here next to
          exactly what you sent them.
        </div>
        <button onClick={onBack} style={{ ...btn(T), marginTop: 18, background: T.card, color: T.textMd, border: "1.5px solid " + T.border }}>Back to the worksheet</button>
      </div>
    );
  }

  return (
    <div style={{ padding: 20 }}>
      {withReplies.map((d) => (
        <div key={d.dealId} style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 2 }}>{(d.sent && d.sent.vehicle) || "This deal"}</div>
          {d.sent ? (
            <div style={{ fontSize: 12, color: T.textSm, marginBottom: 10 }}>
              You sent {money(d.sent.offer)}{d.sent.askingPrice != null ? " against a " + money(d.sent.askingPrice) + " ask" : ""}
            </div>
          ) : null}

          {d.responses.map((r) => <ResponseCard key={r.seq} T={T} sent={d.sent} r={r} />)}

          {d.confirmed ? (
            <div style={{ fontSize: 12.5, color: T.green, background: T.greenL, borderRadius: 8, padding: "8px 11px", marginTop: 8 }}>
              You confirmed this deal with {d.confirmed.dealerLabel || "a dealer"}.
            </div>
          ) : null}
        </div>
      ))}
      <button onClick={onBack} style={{ ...btn(T), background: T.card, color: T.textMd, border: "1.5px solid " + T.border }}>Back to the worksheet</button>
    </div>
  );
}

function ResponseCard({ T, sent, r }) {
  const label = r.kind === "dealer_approved" ? "Approved your offer"
    : r.kind === "dealer_declined" ? "Declined" : "Countered";
  const tone = r.kind === "dealer_approved" ? T.green : r.kind === "dealer_declined" ? T.red : T.amber;
  const bg = r.kind === "dealer_approved" ? T.greenL : r.kind === "dealer_declined" ? T.redL : T.amberL;

  // The same helper that builds Clyde's block. Not a second copy.
  const price = compareField(sent && sent.offer, r.offer, { fmt: money, sentLabel: "you sent" });
  const trade = compareField(sent && sent.trade && sent.trade.allowance, r.tradeAllowance, { fmt: money, sentLabel: "you sent" });
  const rate = compareField(sent && sent.rate, r.rate, { fmt: (x) => x + "%", sentLabel: "you sent" });
  const term = compareField(sent && sent.termMonths, r.termMonths, { fmt: (x) => x + " months", sentLabel: "you sent" });

  return (
    <div style={{ border: "1px solid " + T.border, borderRadius: 12, padding: "13px 15px", marginBottom: 10, background: T.card }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 9 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: T.text }}>{r.dealerLabel || "A dealer"}</div>
        <span style={{ fontSize: 11, fontWeight: 700, color: tone, background: bg, borderRadius: 6, padding: "3px 8px" }}>{label}</span>
      </div>

      {r.vehicleAvailable === false ? (
        <div style={{ fontSize: 12.5, color: T.red, background: T.redL, borderRadius: 7, padding: "7px 10px", marginBottom: 9 }}>
          They say this vehicle is no longer available.
        </div>
      ) : null}

      {price ? <Line T={T} k="Price" c={price} /> : null}
      {trade ? <Line T={T} k="Trade allowance" c={trade} /> : null}
      {rate ? <Line T={T} k="Rate" c={rate} /> : null}
      {term ? <Line T={T} k="Term" c={term} /> : null}

      {r.incentives && r.incentives.length ? (
        <div style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid " + T.border }}>
          <div style={{ fontSize: 11.5, color: T.textSm, marginBottom: 5 }}>Incentives they say apply</div>
          {r.incentives.map((i, n) => (
            <div key={n} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12.5, color: T.text, padding: "3px 0" }}>
              {/* Dealer-written. Rendered as plain text — React escapes it. */}
              <span>{i.label || "Unnamed"}{i.expiresOn ? <span style={{ color: T.textSm }}> · expires {i.expiresOn}</span> : null}</span>
              <span style={{ fontWeight: 700 }}>{money(i.amount) || (i.rate != null ? i.rate + "%" : "—")}</span>
            </div>
          ))}
        </div>
      ) : null}

      {r.note ? (
        <div style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid " + T.border }}>
          <div style={{ fontSize: 11.5, color: T.textSm, marginBottom: 4 }}>What they wrote</div>
          {/* Raw, exactly as typed. The household is judging this dealer and
              needs their actual words; Clyde's copy is fenced separately
              because that one enters a model's context. */}
          <div style={{ fontSize: 12.5, color: T.text, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{r.note}</div>
        </div>
      ) : null}

      <div style={{ fontSize: 11, color: T.textSm, marginTop: 9 }}>
        {fmtDate(r.at) || "recently"}{r.stockNumber ? " · stock " + r.stockNumber : ""}
      </div>
    </div>
  );
}

function Line({ T, k, c }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12.5, padding: "3px 0" }}>
      <span style={{ color: T.textSm, whiteSpace: "nowrap" }}>{k}</span>
      <span style={{ color: c.changed ? T.text : T.textMd, fontWeight: c.changed ? 700 : 400, textAlign: "right" }}>{c.text}</span>
    </div>
  );
}

function Row({ T, k, v }) {
  if (v == null || v === "") return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12.5, padding: "3px 0" }}>
      <span style={{ color: T.textSm }}>{k}</span>
      <span style={{ color: T.text, fontWeight: 600, textAlign: "right" }}>{v}</span>
    </div>
  );
}

function btn(T) {
  return { width: "100%", padding: "10px 0", background: T.slateL, color: T.textMd, border: "1px solid " + T.border, borderRadius: 9, fontWeight: 700, fontSize: 13 };
}

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
